import type { PageBlock } from "@pdfking/book3d"
import type { ColorComponent, Distance, Margins } from "@pdfking/math"
import type { PageNumber } from "@pdfking/renderer-api"
import type { ContentRenderer } from "@pdfking/templates"

// Plain white stock — nothing in a dispatch describes the paper's own color,
// so every block synthesized here shares this one.
const PAPER_COLOR = {
  red: 1 as ColorComponent,
  green: 1 as ColorComponent,
  blue: 1 as ColorComponent,
  alpha: 1 as ColorComponent,
}

const PAPER_ROUGHNESS = 0.5

/**
 * One block of the 3D book, from the only things a dispatch actually knows
 * about it: how many sheets, which sides print, and the block's size. The
 * paper's own appearance is fixed here rather than at each call site, so the
 * imposed book ({@link BuildMeta3D}) and the pass-through one
 * ({@link CreateFilesBook}) can't drift apart — and neither has to assemble a
 * `PageBlock` behind a cast.
 */
export const BuildBookBlock = (opts: {
  print_front: boolean
  print_rear: boolean
  sheets: PageNumber
  /** The runs its sheets make, each showing the same pages; one by default. */
  copies?: number
  width: Distance
  height: Distance
  depth: Distance
  /** What its component's grow draws its faces as. */
  renderer: ContentRenderer
  /** Whether its component's grow is a cover. */
  is_cover: boolean
  /** Its component's security margins. */
  security: Margins<Distance>
  /** The grown page's pieces; none for a plain page. */
  pieces?: PageBlock["pieces"]
  /** How the grown page folds; none for a flat one. */
  folds?: PageBlock["folds"]
  /** The outline its die cuts the grown page to; none for a page cut to
   * its cells. */
  outline?: PageBlock["outline"]
  /** What its die cuts out within the outline. */
  holes?: PageBlock["holes"]
}): PageBlock => ({
  paper_color: PAPER_COLOR,
  paper_roughness: PAPER_ROUGHNESS,
  print_front: opts.print_front,
  print_rear: opts.print_rear,
  renderer: opts.renderer,
  is_cover: opts.is_cover,
  security: opts.security,
  sheets: opts.sheets,
  copies: opts.copies ?? 1,
  pieces: opts.pieces ?? [],
  folds: opts.folds ?? [],
  outline: opts.outline ?? [],
  holes: opts.holes ?? [],
  size: { depth: opts.depth, height: opts.height, width: opts.width },
})
